import { DateTime } from 'luxon';
import { moment } from '../types';
import { Enumeration } from '../types/enumeration';

const pluralRules = new Intl.PluralRules('en-US', {
  type: 'ordinal',
});
const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
]);

export const SystemMinimumDate = '2000-01-01T00:00:00.00';

export class DateTimeFormat extends Enumeration {
  static Short = new DateTimeFormat(1, 'Short', 'dd/MM/yyyy');
  static DateTime = new DateTimeFormat(2, 'DateTime', 'dd/MM/yyyy HH:mm:ss');
  static Filename = new DateTimeFormat(3, 'Filename', 'dd-MM-yyyy HH-mm-ss');
  static ChartMonthDate = new DateTimeFormat(4, 'ChartMonthDate', 'MMM-yy');
  static ChartDayDate = new DateTimeFormat(5, 'ChartDayDate', 'dd-MMM-yy');
  static DailyDate = new DateTimeFormat(6, 'DailyDate', 'dd-MMM-yyyy');
  static MonthlyDate = new DateTimeFormat(7, 'MonthlyDate', 'MMM yyyy');
  static DayDate = new DateTimeFormat(8, 'DayDate', 'dd MMM yyyy');
  static MonthDate = new DateTimeFormat(9, 'MonthDate', 'MMMM yyyy');
  static LetterDate = new DateTimeFormat(10, 'LetterDate', 'dd MMM yyyy');
  static LetterShortDate = new DateTimeFormat(11, 'LetterShortDate', 'd MMM yyyy');
  static DateTimeShort = new DateTimeFormat(12, 'DateTimeShort', 'dd/MM/yyyy hh:mm a');
  static BackendDate = new DateTimeFormat(13, 'BackendDate', 'yyyy-MM-dd');
  static FinancialDate = new DateTimeFormat(14, 'FinancialDate', 'dd MMMM yyyy');
  static Year = new DateTimeFormat(15, 'Year', 'yyyy');
}

export const getLocalDateTimeNow = (format: DateTimeFormat): moment => DateTime.now().toFormat(format.displayName);

export const getLocalDateTime = (
  isoDateTimeOrDate: moment | Date | undefined,
  format: DateTimeFormat,
  isUtc = false
): moment => {
  if (!isoDateTimeOrDate) return '';

  let dateTime: DateTime;

  if (isoDateTimeOrDate instanceof Date) {
    dateTime = isUtc
      ? DateTime.fromJSDate(isoDateTimeOrDate, { zone: 'utc' }).toLocal()
      : DateTime.fromJSDate(isoDateTimeOrDate);
  } else {
    dateTime = isUtc
      ? DateTime.fromISO(isoDateTimeOrDate, { zone: 'utc' }).toLocal()
      : DateTime.fromISO(isoDateTimeOrDate);
  }

  if (format.id === DateTimeFormat.LetterDate.id) {
    return `${dateTime.day}${suffixes.get(pluralRules.select(dateTime.day))} ${dateTime.monthLong} ${dateTime.year}`;
  }
  return dateTime.toFormat(format.displayName);
};

export const getUtcDateTime = (isoDateTime: moment | undefined, format: DateTimeFormat, isUtc = false): moment => {
  if (!!isoDateTime && format.id !== DateTimeFormat.LetterDate.id) {
    return isUtc
      ? DateTime.fromFormat(isoDateTime, format.displayName).toISO()
      : DateTime.fromFormat(isoDateTime, format.displayName).toUTC().toISO();
  }
  return '';
};

export const datePickerDateToLuxonDate = (datePickerDate: string) => {
  return DateTime.fromJSDate(new Date(datePickerDate));
};

export const getFinancialYearStart = () => {
  const currentDate = DateTime.now();
  const thisFinancialYearStart =
    currentDate.month >= 7
      ? currentDate.set({ month: 7 }).startOf('month')
      : currentDate.minus({ year: 1 }).set({ month: 7 }).startOf('month');
  return thisFinancialYearStart.toISODate();
};

/**
 * Use this when dealing with Date values that must always be in Sydney time.
 * @param dateObj A system date object (local time).
 * @returns A "pivot" luxon DateTime object. Pivot times are fixed to midday UTC.
 */
export const createPivotDateTimeFromDate = (dateObj: Date): DateTime => {
  const sydneyOffsetMinutes = 660; // +11:00
  const timeInDstSydney = DateTime.fromJSDate(dateObj, { zone: 'UTC' }).plus({ minutes: sydneyOffsetMinutes });

  const pivotTime = DateTime.utc(
    timeInDstSydney.year,
    timeInDstSydney.month,
    timeInDstSydney.day,
    12, 0, 0
  );

  return pivotTime;
};

/**
 * Calendar component gives us a luxon DateTime object, which on toString becomes "2025-10-21".
 * This is a Sydney date already so we turn it into a pivot time by appending 'T12:00:00Z'.
 * @param calendarDate A luxon DateTime object in Sydney time zone.
 */
export const fromSydneyDateTimeToPivotDate = (calendarDate: DateTime): Date => {
  // The first 10 characters is 2025-10-21.
  const dateOnlyStr = calendarDate.toString().substring(0, 10);
  return new Date(`${dateOnlyStr}T12:00:00Z`);
};

export const isCalendarYearRange = (dateFrom: DateTime, dateTo: DateTime): boolean => {
  return dateFrom.year === dateTo.year && 
         dateFrom.hasSame(dateFrom.startOf('year'), 'day') && 
         dateTo.hasSame(dateTo.endOf('year'), 'day');
};

export const isFinancialYearRange = (dateFrom: DateTime, dateTo: DateTime): boolean => {
  return dateFrom.month === 7 && 
         dateFrom.day === 1 && 
         dateTo.month === 6 && 
         dateTo.day === 30 && 
         dateTo.year === dateFrom.year + 1;
};
